import { Field, TextInput, SelectField, TextAreaField, ChoiceGrid } from "./FormFields";
import MultiSearchSelect from "./MultiSearchSelect";
import {
  RELIGIOUS_BACKGROUNDS,
  SCHOOL_OF_THOUGHT,
  RELIGIOUS_PRACTICE,
  COMPLEXION,
  DIETS,
  HABIT_FREQUENCY,
  SPOKEN_LANGUAGES,
  FORM_COUNTRIES,
  FAMILY_TYPES,
  FAMILY_VALUES,
  PREF_MARITAL,
  PREF_RELIGIOUS,
  PREF_CHILDREN,
} from "@/lib/profileForm";
import { AGE_OPTIONS } from "@/lib/profiles";

export const StepReligious = ({ data, set, errors }) => {
  const bgs = data.religiousBackgrounds || [];
  const schools = data.schoolOfThought || [];

  const toggleBackground = (opt) =>
    set(
      "religiousBackgrounds",
      bgs.includes(opt) ? bgs.filter((o) => o !== opt) : [...bgs, opt],
    );

  return (
    <div className="cp-grid cp-grid-1">
      <Field label="Religious Background — Select all that apply">
        <ChoiceGrid
          multiple
          options={RELIGIOUS_BACKGROUNDS}
          value={bgs}
          onChange={toggleBackground}
          name="Religious Background"
          testIdPrefix="cp-rel-bg"
        />
      </Field>

      <Field label="School of Thought / Religious Affiliation — Select all that apply">
        <MultiSearchSelect
          options={SCHOOL_OF_THOUGHT.filter((o) => o !== "Other")}
          pinnedOptions={["Other"]}
          value={schools}
          onChange={(v) => set("schoolOfThought", v)}
          placeholder="Select School of Thought / Religious Affiliation"
          testId="cp-school-of-thought"
          inlineChips
        />
      </Field>
      {schools.includes("Other") && (
        <Field
          label="Enter your School of Thought / Religious Affiliation"
          required
          error={errors.schoolOfThoughtOther}
        >
          <input
            className={`cp-input${errors.schoolOfThoughtOther ? " has-error" : ""}`}
            placeholder="e.g. Barelvi tradition of a particular Khanqah"
            value={data.schoolOfThoughtOther}
            data-testid="cp-school-of-thought-other"
            onChange={(e) => set("schoolOfThoughtOther", e.target.value)}
          />
        </Field>
      )}

      <div className="cp-grid">
        <SelectField
          label="Religious Practice"
          id="cp-rel-practice"
          options={RELIGIOUS_PRACTICE}
          value={data.religiousPractice}
          onChange={(v) => set("religiousPractice", v)}
          error={errors.religiousPractice}
        />
        {data.religiousPractice === "Other" && (
          <TextInput
            label="Please specify your religious practice"
            required
            id="cp-rel-practice-other"
            placeholder="Describe your religious practice in your own words"
            value={data.religiousPracticeOther}
            onChange={(e) => set("religiousPracticeOther", e.target.value)}
            error={errors.religiousPracticeOther}
          />
        )}
      </div>
    </div>
  );
};

export const StepLifestyle = ({ data, set, errors }) => (
  <div className="cp-grid">
    <TextInput
      label="Height (cm)"
      id="cp-height"
      type="number"
      min="120"
      max="210"
      placeholder="e.g. 165"
      value={data.height}
      onChange={(e) => set("height", e.target.value)}
      error={errors.height}
    />
    <TextInput
      label="Weight (kg)"
      id="cp-weight"
      type="number"
      min="20"
      max="300"
      placeholder="e.g. 65"
      value={data.weight}
      onChange={(e) => set("weight", e.target.value)}
      error={errors.weight}
    />
    <SelectField
      label="Complexion / Skin Tone"
      id="cp-complexion"
      options={COMPLEXION}
      value={data.complexion}
      onChange={(v) => set("complexion", v)}
      error={errors.complexion}
    />
    {data.complexion === "Other" && (
      <TextInput
        label="Enter your Complexion / Skin Tone"
        required
        id="cp-complexion-other"
        placeholder="Describe your complexion in your own words"
        value={data.complexionOther}
        onChange={(e) => set("complexionOther", e.target.value)}
        error={errors.complexionOther}
      />
    )}
    <SelectField
      label="Diet"
      id="cp-diet"
      options={DIETS}
      value={data.diet}
      onChange={(v) => set("diet", v)}
      error={errors.diet}
    />
    <SelectField
      label="Smoking"
      id="cp-smoking"
      options={HABIT_FREQUENCY}
      value={data.smoking}
      onChange={(v) => set("smoking", v)}
      error={errors.smoking}
    />
    <SelectField
      label="Tobacco"
      id="cp-tobacco"
      options={HABIT_FREQUENCY}
      value={data.tobacco}
      onChange={(v) => set("tobacco", v)}
      error={errors.tobacco}
    />
    <SelectField
      label="Alcohol"
      id="cp-alcohol"
      options={HABIT_FREQUENCY}
      value={data.alcohol}
      onChange={(v) => set("alcohol", v)}
      error={errors.alcohol}
    />
    <Field label="Languages — Select all that apply">
      <MultiSearchSelect
        options={SPOKEN_LANGUAGES.filter((o) => o !== "Other")}
        pinnedOptions={["Other"]}
        value={data.languages || []}
        onChange={(v) => set("languages", v)}
        placeholder="Select Languages"
        testId="cp-languages"
      />
    </Field>
    {(data.languages || []).includes("Other") && (
      <TextInput
        label="Enter your language"
        required
        id="cp-languages-other"
        placeholder="e.g. Sindhi, Konkani"
        value={data.languagesOther}
        onChange={(e) => set("languagesOther", e.target.value)}
        error={errors.languagesOther}
      />
    )}
    <div className="cp-grid-1">
      <TextAreaField
        label="Hobbies & Interests"
        id="cp-hobbies"
        rows={6}
        placeholder="Tell us about your hobbies, interests and activities you enjoy..."
        value={data.hobbies}
        onChange={(v) => set("hobbies", v)}
        error={errors.hobbies}
        testId="cp-hobbies"
      />
    </div>
  </div>
);

export const StepFamily = ({ data, set, errors }) => (
  <div className="cp-grid">
    <SelectField
      label="Family Type"
      id="cp-family-type"
      options={FAMILY_TYPES}
      value={data.familyType}
      onChange={(v) => set("familyType", v)}
      error={errors.familyType}
    />
    <SelectField
      label="Family Values"
      id="cp-family-values"
      options={FAMILY_VALUES}
      value={data.familyValues}
      onChange={(v) => set("familyValues", v)}
      error={errors.familyValues}
    />
    <TextInput
      label="Father’s Occupation"
      id="cp-father-occ"
      placeholder="Father’s occupation"
      value={data.fatherOccupation}
      onChange={(e) => set("fatherOccupation", e.target.value)}
      error={errors.fatherOccupation}
    />
    <TextInput
      label="Mother’s Occupation"
      id="cp-mother-occ"
      placeholder="Mother’s occupation"
      value={data.motherOccupation}
      onChange={(e) => set("motherOccupation", e.target.value)}
      error={errors.motherOccupation}
    />
    <TextInput
      label="Siblings"
      id="cp-siblings"
      placeholder="e.g. 1 brother, 2 sisters"
      value={data.siblings}
      onChange={(e) => set("siblings", e.target.value)}
      error={errors.siblings}
    />
    <TextInput
      label="Family Location"
      id="cp-family-location"
      placeholder="e.g. Hyderabad, India"
      value={data.familyLocation}
      onChange={(e) => set("familyLocation", e.target.value)}
      error={errors.familyLocation}
    />
    <div className="cp-grid-1">
      <TextAreaField
        label="Brief Family Introduction"
        id="cp-family-intro"
        rows={4}
        limit={500}
        placeholder="A few lines about your family."
        value={data.familyIntro}
        onChange={(v) => set("familyIntro", v)}
        error={errors.familyIntro}
        testId="cp-family-intro"
      />
    </div>
  </div>
);

export const StepPreferences = ({ data, set, errors }) => {
  const statuses = data.prefMaritalStatuses || [];

  const onMaritalChange = (next) => {
    const hadAny = statuses.includes("Any");
    const hasAny = next.includes("Any");
    if (!hadAny && hasAny) {
      set("prefMaritalStatuses", ["Any"]);
      return;
    }
    if (hadAny && hasAny && next.length > 1) {
      set("prefMaritalStatuses", next.filter((o) => o !== "Any"));
      return;
    }
    set("prefMaritalStatuses", next);
  };

  return (
    <div>
      <div className="cp-grid">
        <SelectField
          label="Preferred Age From"
          required
          id="cp-pref-age-from"
          options={AGE_OPTIONS}
          value={data.prefAgeFrom}
          onChange={(v) => set("prefAgeFrom", v)}
          error={errors.prefAgeFrom}
        />
        <SelectField
          label="Preferred Age To"
          required
          id="cp-pref-age-to"
          options={AGE_OPTIONS}
          value={data.prefAgeTo}
          onChange={(v) => set("prefAgeTo", v)}
          error={errors.prefAgeTo}
        />
        <Field label="Preferred Country / Location" required error={errors.prefLocations}>
          <MultiSearchSelect
            options={FORM_COUNTRIES}
            allowAdd
            value={data.prefLocations || []}
            onChange={(v) => set("prefLocations", v)}
            placeholder="Select or add countries / locations"
            testId="cp-pref-locations"
            inlineChips
            cleanChips
          />
        </Field>
        <Field label="Preferred Marital Status" required error={errors.prefMaritalStatuses}>
          <MultiSearchSelect
            options={PREF_MARITAL.filter((o) => o !== "Any")}
            pinnedOptions={["Any"]}
            value={statuses}
            onChange={onMaritalChange}
            placeholder="Select Preferred Marital Status"
            testId="cp-pref-marital"
            inlineChips
            cleanChips
          />
        </Field>
        <TextInput
          label="Preferred Education"
          id="cp-pref-education"
          placeholder="e.g. Graduate or above"
          value={data.prefEducation}
          onChange={(e) => set("prefEducation", e.target.value)}
          error={errors.prefEducation}
        />
        <TextInput
          label="Preferred Profession"
          id="cp-pref-profession"
          placeholder="e.g. Doctor, Teacher — or leave open"
          value={data.prefProfession}
          onChange={(e) => set("prefProfession", e.target.value)}
          error={errors.prefProfession}
        />
        <SelectField
          label="Religious Preferences"
          id="cp-pref-religious"
          options={PREF_RELIGIOUS}
          placeholder="Any"
          value={data.prefReligious}
          onChange={(v) => set("prefReligious", v)}
          error={errors.prefReligious}
        />
        <TextInput
          label="Preferred Height"
          id="cp-pref-height"
          placeholder="e.g. 5 ft 4 in and above"
          value={data.prefHeight}
          onChange={(e) => set("prefHeight", e.target.value)}
          error={errors.prefHeight}
        />
        <SelectField
          label="Children Preference"
          id="cp-pref-children"
          options={PREF_CHILDREN}
          value={data.prefChildren}
          onChange={(v) => set("prefChildren", v)}
          error={errors.prefChildren}
        />
      </div>
      <div className="cp-grid cp-grid-1 cp-mt">
        <TextAreaField
          label="What I am looking for in my life partner"
          id="cp-partner-vision"
          rows={6}
          limit={800}
          placeholder="Describe the person you hope to share your life with — character, faith, nature and dreams."
          value={data.partnerVision}
          onChange={(v) => set("partnerVision", v)}
          error={errors.partnerVision}
          testId="cp-partner-vision"
        />
      </div>
    </div>
  );
};
